import type {
  RemoveItem,
  ItemWithId,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useCallback } from "react"

import { useUpsertCollectionItem } from "src/hooks/rxdb"
import type { CollectionKey } from "src/types"
import { noticeError } from "src/utils"

export function useDeleteCollectionItem<Key extends string, Item extends ItemWithId, TransformedItem = Item>(
  collectionKey: CollectionKey,
  responseKey: Key,
  useRemoveItem: () => RemoveItem<Item, Key> | undefined,
  transform: (i: Item) => TransformedItem,
) {
  const deleteItem = useRemoveItem()
  const upsertItem = useUpsertCollectionItem<TransformedItem>(collectionKey)

  return useCallback(
    async (id: Item["id"]) => {
      try {
        if (!deleteItem) {
          throw new Error(`cannot delete ${responseKey} for unauthorized user`)
        }

        const response = await deleteItem(id)
        const item = response.data[responseKey]
        await upsertItem(transform(item))
        return response
      } catch (e) {
        noticeError(
          e,
          "DeleteCollectionItem",
          {
            [`${responseKey}Id`]: id,
            collectionItem: responseKey,
            collectionKey,
          },
        )

        throw e
      }
    },
    [
      collectionKey,
      responseKey,
      deleteItem,
      transform,
      upsertItem,
    ],
  )
}
